/* ============================================================
 * Health Claim Footnotes — robust anchor centering
 *
 * Badges link to this section with a plain in-page anchor
 * (e.g. <a href="#health-claims">). Two things break the native
 * jump:
 *   1. The page sets `scroll-behavior: smooth`, so the browser
 *      animates toward the target.
 *   2. The <product-recommendations> section directly above the
 *      target is empty on load and only fetches + injects its
 *      cards when it nears the viewport. That injection happens
 *      *after* the jump is computed, grows the page, and pushes
 *      the target down — so the user lands above it, then sees it
 *      snap once the recommendations appear.
 *
 * Fix: when an anchor click (or an initial-load hash) resolves to
 * a `.health-claim-footnotes` section, eagerly load any empty
 * product-recommendations above it, then keep the section
 * vertically centered until those loads have settled and the
 * layout stops moving. Any genuine user scroll cancels it.
 * ============================================================ */
(function () {
  'use strict';

  if (window.__hcfAnchorInit) return;
  window.__hcfAnchorInit = true;

  var MAX_MS = 4000; // hard cap on the settle window
  var STABLE_FRAMES = 6; // stop once the target stops moving for this many frames

  // Document-relative scroll position that vertically centers `el`.
  function targetTop(el) {
    var rect = el.getBoundingClientRect();
    var absTop = rect.top + window.pageYOffset;
    var extra = window.innerHeight - rect.height;
    var top = extra > 0 ? absTop - extra / 2 : absTop - 24;
    return Math.max(0, top);
  }

  // Resolve a hash (e.g. "#health-claims") to its health-claim section.
  function sectionFor(hash) {
    if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return null;
    var id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (e) {
      id = hash.slice(1);
    }
    var el = document.getElementById(id);
    if (!el) return null;
    if (el.classList.contains('health-claim-footnotes')) return el;
    return el.closest('.health-claim-footnotes');
  }

  // Empty (not-yet-fetched) product-recommendations that sit above the target
  // and therefore shift it down once they load.
  function pendingRecommendationsAbove(target) {
    var pending = [];
    var nodes = document.querySelectorAll('product-recommendations');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var isBefore = target.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING;
      if (isBefore && el.children.length === 0 && el.dataset && el.dataset.url) {
        pending.push(el);
      }
    }
    return pending;
  }

  // Fetch + inject a product-recommendations block now (same contract as the
  // theme's own lazy loader in global.js). Always resolves, so one slow/failed
  // request can't stall centering.
  function ensureLoaded(el) {
    return new Promise(function (resolve) {
      if (el.children.length > 0) {
        resolve();
        return;
      }

      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        resolve();
      }

      var timer = setTimeout(finish, 2500);

      fetch(el.dataset.url)
        .then(function (response) {
          return response.text();
        })
        .then(function (text) {
          if (el.children.length === 0) {
            var wrapper = document.createElement('div');
            wrapper.innerHTML = text;
            var recommendations = wrapper.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              el.innerHTML = recommendations.innerHTML;
            }
          }
          clearTimeout(timer);
          finish();
        })
        .catch(function () {
          clearTimeout(timer);
          finish();
        });
    });
  }

  function centerOn(target) {
    var root = document.documentElement;
    var prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto'; // force instant scrolling for the loop

    var done = false;
    var listenerOpts = { passive: true };

    function cleanup() {
      if (done) return;
      done = true;
      root.style.scrollBehavior = prevBehavior;
      window.removeEventListener('wheel', onUserScroll, listenerOpts);
      window.removeEventListener('touchmove', onUserScroll, listenerOpts);
      window.removeEventListener('keydown', onKeyScroll, true);
    }

    function onUserScroll() {
      cleanup();
    }

    function onKeyScroll(event) {
      var keys = [' ', 'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (keys.indexOf(event.key) !== -1) cleanup();
    }

    // Any genuine user scroll intent cancels the re-centering.
    window.addEventListener('wheel', onUserScroll, listenerOpts);
    window.addEventListener('touchmove', onUserScroll, listenerOpts);
    window.addEventListener('keydown', onKeyScroll, true);

    // Kick off the lazy sections above the target so their growth happens now,
    // while we hold the section centered, rather than after we've stopped.
    var pending = pendingRecommendationsAbove(target);
    var loadsSettled = pending.length === 0;
    if (!loadsSettled) {
      Promise.all(pending.map(ensureLoaded)).then(function () {
        loadsSettled = true;
      });
    }

    window.scrollTo(0, targetTop(target));

    var start = performance.now();
    var lastOffset = null;
    var stable = 0;

    function tick(now) {
      if (done) return;

      var rect = target.getBoundingClientRect();
      var offset = Math.round(rect.top + window.pageYOffset);

      if (lastOffset !== null && offset === lastOffset) {
        stable += 1;
      } else {
        stable = 0;
      }
      lastOffset = offset;

      window.scrollTo(0, targetTop(target));

      var settledEnough = loadsSettled && stable >= STABLE_FRAMES;
      if (settledEnough || now - start > MAX_MS) {
        cleanup();
        return;
      }
      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  document.addEventListener(
    'click',
    function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var link = target.closest('a[href*="#"]');
      if (!link) return;

      // Same-page anchors only.
      if (link.origin !== window.location.origin || link.pathname !== window.location.pathname) return;

      var section = sectionFor(link.hash);
      if (!section) return;

      event.preventDefault();
      if (window.history && typeof history.pushState === 'function') {
        history.pushState(null, '', link.hash);
      }
      centerOn(section);
    },
    false
  );

  // Center a hash that is already present when the page loads (deep link / reload).
  function handleInitialHash() {
    var section = sectionFor(window.location.hash);
    if (section) {
      requestAnimationFrame(function () {
        centerOn(section);
      });
    }
  }

  if (document.readyState === 'complete') {
    handleInitialHash();
  } else {
    window.addEventListener('load', handleInitialHash);
  }
})();
